import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import db from "./Firebase";

const MONTH_CONFIG = [
  {
    key: "jan_2026",
    label: "Jan 2026",
    start: new Date(2026, 0, 1),
    end: new Date(2026, 1, 1),
  },
  {
    key: "feb_2026",
    label: "Feb 2026",
    start: new Date(2026, 1, 1),
    end: new Date(2026, 2, 1),
  },
  {
    key: "mar_2026",
    label: "Mar 2026",
    start: new Date(2026, 2, 1),
    end: new Date(2026, 3, 1),
  },
];

const EMPTY_MONTH_DATA = {
  portfolioCreated: 0,
  portfolioVerified: 0,
  loanApplied: 0,
  loanDisbursed: 0,
  growPackages: 0,
  appDownloads: 0,
};

const METRIC_ROWS = [
  { key: "portfolioCreated", label: "Portfolios Created" },
  { key: "portfolioVerified", label: "Portfolios Verified" },
  { key: "loanApplied", label: "RG Applied" },
  { key: "loanDisbursed", label: "RG Disbursed" },
  { key: "growPackages", label: "Grow Packages Serviced" },
  { key: "appDownloads", label: "App Downloads" },
];

const normalizeValue = (value) => String(value || "").trim().toLowerCase();

function AnalyticsPreview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analyticsData, setAnalyticsData] = useState(() =>
    MONTH_CONFIG.reduce((acc, month) => {
      acc[month.key] = { ...EMPTY_MONTH_DATA };
      return acc;
    }, {})
  );

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    const storedPassword = localStorage.getItem("password");
    const storedRemember = localStorage.getItem("remember");

    if (storedRemember === "false" || !storedEmail || !storedPassword) {
      navigate("/signin");
    }
  }, [navigate]);

  useEffect(() => {
    let active = true;

    const fetchCollectionData = async (collectionName, dateField, start, end) => {
      const snapshot = await db
        .collection(collectionName)
        .where(dateField, ">=", start)
        .where(dateField, "<", end)
        .get();

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    };

    const fetchAnalytics = async () => {
      setLoading(true);
      setError("");

      try {
        const monthResults = await Promise.all(
          MONTH_CONFIG.map(async (month) => {
            const [portfolios, loans, disbursedLoans, orders, downloads] = await Promise.all([
              fetchCollectionData(
                "nv_portfolio_details",
                "created_date",
                month.start,
                month.end
              ),
              fetchCollectionData("nv_loan", "created_date", month.start, month.end),
              fetchCollectionData(
                "nv_loan",
                "loan_disbursement_date",
                month.start,
                month.end
              ),
              fetchCollectionData("nv_orders_data", "order_date", month.start, month.end),
              fetchCollectionData(
                "personal_details",
                "created_date",
                month.start,
                month.end
              ),
            ]);

            return {
              key: month.key,
              data: {
                portfolioCreated: portfolios.length,
                portfolioVerified: portfolios.filter(
                  (item) => normalizeValue(item.is_published) === "yes"
                ).length,
                loanApplied: loans.length,
                loanDisbursed: disbursedLoans.filter(
                  (item) => normalizeValue(item.final_status) === "disbursed"
                ).length,
                growPackages: orders.length,
                appDownloads: downloads.length,
              },
            };
          })
        );

        if (!active) {
          return;
        }

        const nextData = monthResults.reduce((acc, monthResult) => {
          acc[monthResult.key] = monthResult.data;
          return acc;
        }, {});

        setAnalyticsData(nextData);
      } catch (fetchError) {
        console.error("Error fetching analytics preview:", fetchError);

        if (!active) {
          return;
        }

        setError("Unable to load analytics preview right now.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchAnalytics();

    return () => {
      active = false;
    };
  }, []);

  const summaryCards = useMemo(
    () =>
      MONTH_CONFIG.map((month) => {
        const monthData = analyticsData[month.key] || EMPTY_MONTH_DATA;

        return {
          label: month.label,
          total:
            monthData.portfolioCreated +
            monthData.portfolioVerified +
            monthData.loanApplied +
            monthData.loanDisbursed +
            monthData.growPackages +
            monthData.appDownloads,
        };
      }),
    [analyticsData]
  );

  const metricTotals = useMemo(
    () =>
      METRIC_ROWS.reduce((acc, metric) => {
        acc[metric.key] = MONTH_CONFIG.reduce(
          (sum, month) => sum + (analyticsData[month.key]?.[metric.key] ?? 0),
          0
        );
        return acc;
      }, {}),
    [analyticsData]
  );

  const overallTotals = useMemo(() => {
    const monthlyTotals = MONTH_CONFIG.reduce((acc, month) => {
      acc[month.key] = METRIC_ROWS.reduce(
        (sum, metric) => sum + (analyticsData[month.key]?.[metric.key] ?? 0),
        0
      );
      return acc;
    }, {});

    return {
      ...monthlyTotals,
      allMonths: Object.values(monthlyTotals).reduce((sum, value) => sum + value, 0),
    };
  }, [analyticsData]);

  return (
    <main className="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
      <div className="p-2 d-flex justify-content-between align-items-center">
        <Link to="/">
          <img
            src="/images/menubutton.png"
            className="menu-toggle"
            alt="Menu"
            style={{ height: "2rem" }}
          />
        </Link>
        <div className="d-flex gap-2">
          <Link to="/dashbaordall">
            <button className="btn btn-success btn-sm">Log Dashboard</button>
          </Link>
          <Link to="/analytics_details">
            <button className="btn btn-success btn-sm">User Analytics</button>
          </Link>
        </div>
      </div>

      <div className="container-fluid py-4">
        <div className="card">
          <div className="card-header pb-0">
            <h4 className="mb-1">Analytics Preview</h4>
            <p className="text-sm mb-0">January, February and March 2026</p>
          </div>

          <div className="card-body">
            {loading ? (
              <p className="mb-0">Loading analytics preview...</p>
            ) : error ? (
              <div className="alert alert-danger mb-0" role="alert">
                {error}
              </div>
            ) : (
              <>
                <div className="row">
                  {summaryCards.map((card) => (
                    <div className="col-md-4 mb-3" key={card.label}>
                      <div className="card border shadow-sm h-100">
                        <div className="card-body">
                          <p className="text-sm text-muted mb-1">{card.label}</p>
                          <h3 className="mb-0">{card.total}</h3>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered align-items-center mb-0">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        {MONTH_CONFIG.map((month) => (
                          <th key={month.key}>{month.label}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {METRIC_ROWS.map((metric) => (
                        <tr key={metric.key}>
                          <td className="font-weight-bold">{metric.label}</td>
                          {MONTH_CONFIG.map((month) => (
                            <td key={`${metric.key}-${month.key}`}>
                              {analyticsData[month.key]?.[metric.key] ?? 0}
                            </td>
                          ))}
                          <td>{metricTotals[metric.key] ?? 0}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="font-weight-bold">Total</td>
                        {MONTH_CONFIG.map((month) => (
                          <td key={`total-${month.key}`} className="font-weight-bold">
                            {overallTotals[month.key] ?? 0}
                          </td>
                        ))}
                        <td className="font-weight-bold">{overallTotals.allMonths ?? 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-3">
                  <p className="text-xs text-muted mb-1">
                    Portfolio source: <strong>nv_portfolio_details</strong>
                  </p>
                  <p className="text-xs text-muted mb-1">
                    Loan source: <strong>nv_loan</strong>
                  </p>
                  <p className="text-xs text-muted mb-1">
                    Grow packages source: <strong>nv_orders_data</strong>
                  </p>
                  <p className="text-xs text-muted mb-0">
                    App downloads source: <strong>personal_details</strong>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default AnalyticsPreview;
